export type DevelopmentDetails = {
  title: string;
  summary: string;
  customer: string;
  duration?: string;
  technologies: string[];
  status?: string;
  resultLabel?: string;
  evidenceLabel?: string;
  evidence?: string;
  agents?: { title: string; description: string }[];
};

type Props = {
  details: DevelopmentDetails;
  task: string;
  solution: string;
  result: string;
  ctaLabel: string;
  ctaHref: string;
  onClose: () => void;
};

const DevelopmentCaseDetails = ({ details, task, solution, result, ctaLabel, ctaHref, onClose }: Props) => (
  <article className="min-h-0 w-full overflow-y-auto px-6 py-8 sm:px-9 sm:py-10">
    <header className="border-b border-border pb-6 pr-9">
      <h3 className="font-heading text-2xl leading-tight [overflow-wrap:anywhere] sm:text-3xl">
        {details.title}
      </h3>
      <p className="mt-3 max-w-3xl text-base leading-relaxed text-text-subtle">{details.summary}</p>
      <p className="mt-3 text-sm font-medium text-accent">{details.customer}</p>
      {details.status && <p className="mt-3 text-sm font-medium text-foreground">{details.status}</p>}
    </header>

    <div className="mt-7 grid min-w-0 gap-8 md:grid-cols-2 md:gap-10">
      <div className="min-w-0 space-y-7">
        <section>
          <h4 className="mb-3 font-heading text-xl">Задача</h4>
          <p className="text-[15px] leading-relaxed text-text-subtle [overflow-wrap:anywhere] sm:text-base">{task}</p>
        </section>
        <section>
          <h4 className="mb-3 font-heading text-xl">Решение</h4>
          <p className="whitespace-pre-line text-[15px] leading-relaxed text-text-subtle [overflow-wrap:anywhere] sm:text-base">{solution}</p>
        </section>
      </div>

      <div className="min-w-0 space-y-7">
        <section>
          <h4 className="mb-3 font-heading text-xl">Технологии</h4>
          <p className="text-[15px] leading-relaxed text-text-subtle [overflow-wrap:anywhere] sm:text-base">
            {details.technologies.join(" · ")}
          </p>
          {details.duration && <p className="mt-4 text-[15px] leading-relaxed sm:text-base">
            <span className="font-medium">Срок реализации:</span> {details.duration}
          </p>}
        </section>
        <section className="border-t border-border pt-6">
          <h4 className="mb-3 font-heading text-xl text-accent">
            {details.resultLabel ?? (details.status ? "Заявленные результаты" : "Результат")}
          </h4>
          <ul className="list-disc space-y-3 pl-5 text-[15px] leading-relaxed text-text-subtle sm:text-base">
            {result.split("\n").filter(Boolean).map((item) => <li key={item} className="pl-1 [overflow-wrap:anywhere]">{item}</li>)}
          </ul>
        </section>
        {details.evidence && (
          <section className="border-t border-border pt-6">
            <h4 className="mb-3 font-heading text-xl">{details.evidenceLabel ?? "Условие результата"}</h4>
            <p className="text-[15px] leading-relaxed text-text-subtle [overflow-wrap:anywhere] sm:text-base">{details.evidence}</p>
          </section>
        )}
      </div>
    </div>

    {details.agents?.length && (
      <section className="mt-8 border-t border-border pt-7">
        <h4 className="font-heading text-xl">ИИ-агенты Т1</h4>
        <div className="mt-5 grid min-w-0 gap-x-10 gap-y-6 md:grid-cols-2">
          {details.agents.map((agent, index) => (
            <section key={agent.title} className="min-w-0">
              <h5 className="font-body text-base font-medium leading-snug [overflow-wrap:anywhere]">
                {index + 1}. {agent.title}
              </h5>
              <p className="mt-2 text-[15px] leading-relaxed text-text-subtle [overflow-wrap:anywhere] sm:text-base">{agent.description}</p>
            </section>
          ))}
        </div>
      </section>
    )}

    <a href={ctaHref} onClick={onClose} className="mt-8 inline-flex rounded-full bg-accent px-5 py-2.5 font-body text-sm text-accent-foreground transition-opacity hover:opacity-90">
      {ctaLabel}
    </a>
  </article>
);

export default DevelopmentCaseDetails;
